import React, { useEffect, useContext } from "react";
import { HEADERTEXT_TYPES } from "../functions/postActionTypes";
import {
  IonContent,
  IonLabel,
  IonButton,
  IonInput,
  IonItem,
  IonPage,
  IonRow,
  IonCol,
  IonGrid,
  IonIcon,
  IonTextarea,
  useIonViewWillLeave,
  useIonToast,
} from "@ionic/react";
import { arrowBack } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import "../theme/AppStyles.css";
import { ProfileContext } from "../context/ProfileContext";
import { fx_FB_UpdateUserProfile } from "../functions/Fx_FirebaseUpdateFunctions";
import { fx_FB_RedeemInviteCode, REDEEM_RESULT } from "../functions/Fx_InviteCodes";
import { WerkerSchedulerHeader } from "../components/WerkHeader";

interface ProfileEditProps {
  desktopMode?: {
    isDesktopMode: boolean;
    onComplete: () => void;
  };
}

const Profile_Edit: React.FC<ProfileEditProps> = ({ desktopMode }) => {
  const { profile, buttonColor } = useContext(ProfileContext)
  const history = useHistory();
  const isDesktopMode = desktopMode?.isDesktopMode ?? false;

  const blurFocusedElement = () => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }
  };

  interface ProfileData {
    Uid: string;
    FirstName: string;
    LastName: string;
    Email: string;
    Username: string;
    IsDeleted: boolean;
    Company: string;
    Occupation: string;
    ProfilePicURL: string;
    UserBio: string;
  }
  const [editProfile, setEditProfile] = React.useState<ProfileData>({
    Uid: "",
    FirstName: "",
    LastName: "",
    Email: "",
    Username: "",
    IsDeleted: false,
    Company: "",
    Occupation: "",
    ProfilePicURL: "",
    UserBio: "",
  });


  const completeEditor = () => {
    blurFocusedElement();
    if (desktopMode?.onComplete) {
      desktopMode.onComplete();
    } else {
      history.goBack();
    }
  };

  useIonViewWillLeave(() => {
    blurFocusedElement();
  });

  const [presentToast] = useIonToast();
  const [inviteCodeInput, setInviteCodeInput] = React.useState("");
  const [redeeming, setRedeeming] = React.useState(false);

  const handleRedeemInviteCode = async () => {
    const code = inviteCodeInput.trim();
    if (!code) {
      presentToast({ message: "Enter an invite code first.", duration: 2500, position: "top", color: "warning" });
      return;
    }
    if (!profile?.UserId) return;
    setRedeeming(true);
    try {
      const outcome = await fx_FB_RedeemInviteCode(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company || "",
        profile.ProfilePicURL || "../assets/profilePic.png",
        code
      );
      if (outcome.result === REDEEM_RESULT.Success) {
        setInviteCodeInput("");
        presentToast({
          message: `You've joined ${outcome.schedulerFirstName ?? ""} ${outcome.schedulerLastName ?? ""}'s group.`.replace(/\s+/g, " ").trim(),
          duration: 3000,
          position: "top",
          color: "success",
        });
      } else if (outcome.result === REDEEM_RESULT.AlreadyAssociate) {
        setInviteCodeInput("");
        presentToast({
          message: `You're already in ${outcome.schedulerFirstName ?? ""} ${outcome.schedulerLastName ?? ""}'s group.`.replace(/\s+/g, " ").trim(),
          duration: 2500,
          position: "top",
          color: "medium",
        });
      } else if (outcome.result === REDEEM_RESULT.SelfCode) {
        presentToast({ message: "You can't redeem your own invite code.", duration: 2500, position: "top", color: "warning" });
      } else {
        presentToast({ message: "Invite code not recognized.", duration: 2500, position: "top", color: "warning" });
      }
    } catch (err) {
      presentToast({ message: "Something went wrong. Please try again.", duration: 2500, position: "top", color: "danger" });
    } finally {
      setRedeeming(false);
    }
  };

  const handleSubmit = async (saveClose: string) => {
    if (saveClose === "save") {
      fx_FB_UpdateUserProfile(profile.UserId, editProfile.Company, editProfile.FirstName, editProfile.LastName, editProfile.Occupation, editProfile.UserBio);
    }
    completeEditor();
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setEditProfile(profile);
      } catch (err) {
        // console.log(err);
      }
    };
    fetchUserData();
  }, [profile]);


  const formFields = (
    <>
      <IonRow className="jobGrid">
              <IonCol>
                <IonItem>
                  <IonRow>
                    <IonCol>
                      <IonLabel position="stacked">First Name:</IonLabel>
                      <IonInput
                    autoCapitalize="sentences"
                    type="text"
                    name="FirstName"
                    value={editProfile.FirstName}
                    placeholder="First Name"
                    onIonChange={(e: any) => setEditProfile({
                      ...editProfile,
                      FirstName: e.target.value,
                    })}
                    clearInput                      ></IonInput>
                    </IonCol>
                    <IonCol>
                      <IonLabel position="stacked">Last Name:</IonLabel>
                      <IonInput
                    autoCapitalize="sentences"
                    type="text"
                    name="LastName"
                    value={editProfile.LastName}
                    placeholder="Last Name"
                    onIonChange={(e: any) => setEditProfile({
                      ...editProfile,
                      LastName: e.target.value,
                    })}
                    clearInput                      ></IonInput>
                    </IonCol>
                  </IonRow>
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow className="jobGrid">
              <IonCol>
                <IonItem>
                  <IonLabel position="stacked">Email:</IonLabel>
                  <IonInput
                type="text"
                name="Email"
                value={editProfile.Email}
                placeholder="Email Address"
                onIonChange={(e: any) => setEditProfile({ ...editProfile, Email: e.target.value })}
                clearInput                  ></IonInput>
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow className="jobGrid">
              <IonCol>
                <IonItem>
                  <IonLabel position="stacked">Company/Agency:</IonLabel>
                  <IonInput
                autoCapitalize="sentences"
                type="text"
                name="Company"
                value={editProfile.Company}
                placeholder="Company/Ageny You Work For..."
                onIonChange={(e: any) => setEditProfile({
                  ...editProfile,
                  Company: e.target.value,
                })}
                clearInput                  ></IonInput>
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow className="jobGrid">
              <IonCol>
                <IonItem>
                  <IonLabel position="stacked">Occupation:</IonLabel>
                  <IonInput
                spellCheck="true"
                autoCapitalize="sentences"
                type="text"
                name="Occupation"
                value={editProfile.Occupation}
                placeholder="Your Occupation..."
                onIonChange={(e: any) => setEditProfile({
                  ...editProfile,
                  Occupation: e.target.value,
                })}
                clearInput                  ></IonInput>
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow className="jobGrid">
              <IonCol>
                <IonItem>
                  <IonLabel position="stacked">Bio:</IonLabel>
                  <IonTextarea
                autoCapitalize="sentences"
                spellCheck="true"
                autoGrow={true}
                name="bio"
                value={editProfile.UserBio}
                placeholder="About Me... Counties I werk, ect."
                onIonChange={(e: any) => setEditProfile({
                  ...editProfile,
                  UserBio: e.target.value,
                })}                  ></IonTextarea>
                </IonItem>
              </IonCol>
            </IonRow>

            {!profile?.IsScheduler && (
              <IonRow className="jobGrid">
                <IonCol>
                  <IonItem lines="none" style={{ "--background": "transparent" }}>
                    <IonLabel position="stacked">
                      <strong>Join a Scheduler's Group</strong>
                      <div style={{ fontSize: 12, color: "var(--ion-color-medium)", marginTop: 2 }}>
                        Got an invite code? Enter it here to auto-associate.
                      </div>
                    </IonLabel>
                  </IonItem>
                  <IonItem>
                    <IonInput
                      autoCapitalize="characters"
                      type="text"
                      name="InviteCode"
                      placeholder="e.g. JD1234"
                      value={inviteCodeInput}
                      onIonChange={(e: any) => setInviteCodeInput((e.target.value ?? "").toString().toUpperCase())}
                      clearInput
                    ></IonInput>
                    <IonButton
                      slot="end"
                      onClick={handleRedeemInviteCode}
                      disabled={redeeming || !inviteCodeInput.trim()}
                      color={buttonColor}
                    >
                      {redeeming ? "Joining…" : "Join"}
                    </IonButton>
                  </IonItem>
                </IonCol>
              </IonRow>
            )}
    </>
  );

  const saveAction = (
    <>
      <IonRow>
        <IonCol></IonCol>
        <IonCol size="11">
          <IonButton
            onClick={() => handleSubmit("save")}
            color={buttonColor}
            fill="solid"
            expand="block"
          >
            Save
          </IonButton>
        </IonCol>
        <IonCol></IonCol>
      </IonRow>
      <br></br>
    </>
  );

  const renderDesktopHeader = () => (
    <div
      style={{
        borderBottom: "1px solid var(--ion-color-light-shade)",
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "var(--ion-background-color)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 56,
          padding: "0 12px",
          boxSizing: "border-box",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <IonButton onClick={completeEditor} fill="clear" style={{ margin: 0 }}>
          <IonIcon
            icon={arrowBack}
            size="large"
            color="dark"
          />
        </IonButton>

        <div style={{ flex: 1, textAlign: "center" }}>
          <strong><h2 style={{ margin: 0, lineHeight: "24px" }}>{HEADERTEXT_TYPES.EditProfile}</h2></strong>
        </div>

        <IonButton
          onClick={() => handleSubmit("save")}
          fill="clear"
          color="dark"
          style={{ margin: 0 }}
        >
          <strong>Save</strong>
        </IonButton>
      </div>
    </div>
  );

  if (isDesktopMode) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {renderDesktopHeader()}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          <IonGrid
            style={{ maxWidth: "800px", margin: "0 auto", padding: "16px 0" }}
          >
            {formFields}
          </IonGrid>
        </div>
      </div>
    );
  }

  return (
    <IonPage>
      <WerkerSchedulerHeader title = {HEADERTEXT_TYPES.EditProfile} />
      <IonContent>
        {formFields}
      </IonContent>

      {saveAction}
    </IonPage>
  );
};

export default Profile_Edit;